/**
 * Reference Buttons Component
 * Label and Reference insertion buttons
 */

import React, { useState, useRef, useEffect } from 'react'
import { EditorView } from '@codemirror/view'
import { TagIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { getSelection, getCurrentLine } from '@/utils/latex/insertionHelpers'
import { EditorSelection } from '@codemirror/state'

interface ReferenceButtonsProps {
  view: EditorView | null
  theme?: 'light' | 'dark'
  documentContent?: string // Full document content to extract existing labels
}

const ReferenceButtons: React.FC<ReferenceButtonsProps> = ({ view, theme = 'light', documentContent = '' }) => {
  const [showLabelDialog, setShowLabelDialog] = useState(false)
  const [showRefDialog, setShowRefDialog] = useState(false)
  const [labelName, setLabelName] = useState('')
  const [selectedRef, setSelectedRef] = useState('')
  const labelInputRef = useRef<HTMLInputElement>(null)

  // Extract existing labels from document content
  const existingLabels = React.useMemo(() => {
    if (!documentContent) return []
    const labelRegex = /\\label\{([^}]+)\}/g
    const labels: string[] = []
    let match
    while ((match = labelRegex.exec(documentContent)) !== null) {
      labels.push(match[1])
    }
    return Array.from(new Set(labels)).sort()
  }, [documentContent])

  useEffect(() => {
    if (showLabelDialog && labelInputRef.current) {
      setTimeout(() => labelInputRef.current?.focus(), 100)
    }
  }, [showLabelDialog])

  const handleInsertLabel = () => {
    if (!view || !labelName.trim()) return

    const { from } = getSelection(view)
    const insertText = `\\label{${labelName.trim()}}`
    const cursorPos = from + insertText.length

    view.dispatch({
      changes: {
        from,
        to: from,
        insert: insertText
      },
      selection: {
        anchor: cursorPos
      }
    })

    view.focus()
    setShowLabelDialog(false)
    setLabelName('')
  }

  const handleInsertRef = () => {
    if (!view || !selectedRef.trim()) return

    const { from } = getSelection(view)
    const insertText = `\\ref{${selectedRef.trim()}}`
    const cursorPos = from + insertText.length

    view.dispatch({
      changes: {
        from,
        to: from,
        insert: insertText
      },
      selection: {
        anchor: cursorPos
      }
    })

    view.focus()
    setShowRefDialog(false)
    setSelectedRef('')
  }

  const suggestLabelPrefix = () => {
    if (!view) return 'fig:'
    const line = getCurrentLine(view)
    const lineText = line.text.toLowerCase()
    
    if (lineText.includes('figure') || lineText.includes('\\begin{figure}')) return 'fig:'
    if (lineText.includes('table') || lineText.includes('\\begin{table}')) return 'tab:'
    if (lineText.includes('equation') || lineText.includes('\\begin{equation}')) return 'eq:'
    if (lineText.includes('section')) return 'sec:'
    return ''
  }

  const buttonClass = clsx(
    'p-1.5 rounded transition-colors',
    theme === 'dark'
      ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
      : 'text-gray-800 hover:bg-gray-100 hover:text-gray-900',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  )

  const dialogClass = clsx(
    'fixed inset-0 z-50 flex items-center justify-center',
    theme === 'dark' ? 'bg-black/50' : 'bg-black/30'
  )

  const contentClass = clsx(
    'relative rounded-lg shadow-xl p-6 w-full max-w-md',
    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
  )

  const inputClass = clsx(
    'w-full px-3 py-2 rounded-md border text-sm',
    theme === 'dark'
      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
  )

  return (
    <>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => {
            setLabelName(suggestLabelPrefix())
            setShowLabelDialog(true)
          }}
          disabled={!view}
          className={buttonClass}
          title="Insert label - Create \\label{} for figures, tables, equations, or sections"
          aria-label="Insert label"
        >
          <TagIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => setShowRefDialog(true)}
          disabled={!view}
          className={buttonClass}
          title="Insert reference - Create \\ref{} to reference existing labels"
          aria-label="Insert reference"
        >
          <ArrowPathIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Label Dialog */}
      {showLabelDialog && (
        <div className={dialogClass} onClick={() => setShowLabelDialog(false)}>
          <div className={contentClass} onClick={(e) => e.stopPropagation()}>
            <h3 className={clsx('text-lg font-semibold mb-4', theme === 'dark' ? 'text-gray-100' : 'text-gray-900')}>
              Insert Label
            </h3>
            <input
              ref={labelInputRef}
              type="text"
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleInsertLabel()
                } else if (e.key === 'Escape') {
                  setShowLabelDialog(false)
                }
              }}
              placeholder="fig:myfigure"
              className={inputClass}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowLabelDialog(false)}
                className={clsx('px-4 py-2 rounded-md text-sm', theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100')}
              >
                Cancel
              </button>
              <button
                onClick={handleInsertLabel}
                disabled={!labelName.trim()}
                className="px-4 py-2 rounded-md text-sm bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reference Dialog */}
      {showRefDialog && (
        <div className={dialogClass} onClick={() => setShowRefDialog(false)}>
          <div className={contentClass} onClick={(e) => e.stopPropagation()}>
            <h3 className={clsx('text-lg font-semibold mb-4', theme === 'dark' ? 'text-gray-100' : 'text-gray-900')}>
              Insert Reference
            </h3>
            {existingLabels.length > 0 ? (
              <select
                value={selectedRef}
                onChange={(e) => setSelectedRef(e.target.value)}
                className={inputClass}
              >
                <option value="">Select a label...</option>
                {existingLabels.map((label) => (
                  <option key={label} value={label}>
                    {label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={selectedRef}
                onChange={(e) => setSelectedRef(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleInsertRef()
                  } else if (e.key === 'Escape') {
                    setShowRefDialog(false)
                  }
                }}
                placeholder="fig:myfigure"
                className={inputClass}
              />
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowRefDialog(false)}
                className={clsx('px-4 py-2 rounded-md text-sm', theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100')}
              >
                Cancel
              </button>
              <button
                onClick={handleInsertRef}
                disabled={!selectedRef.trim()}
                className="px-4 py-2 rounded-md text-sm bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ReferenceButtons

